import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import TextEditor from "../TextEditor";
import {
  TrashIcon,
  PhotoIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";
import ImageManagerModal from "./ImageManagerModal";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const templateSchema = Yup.object().shape({
  title: Yup.string().trim().required("Tiêu đề không được để trống"),
  topic: Yup.string().trim().required("Chủ đề không được để trống"),
  time: Yup.string().trim().required("Thời gian không được để trống"),
  location: Yup.string().trim().required("Địa điểm không được để trống"),
  postId: Yup.string().trim().required("Thông báo không được để trống"),
});
const imageSchema = Yup.object().shape({
  postId: Yup.string().trim().required("Thông báo không được để trống"),
  eventInvationImage: Yup.string()
    .trim()
    .required("Vui lòng chọn ảnh thiệp mời"),
});

const EventModal = ({ isOpen, data, eventPostId, onClose }) => {
  const [formData, setFormData] = useState({
    title: "",
    topic: "",
    time: "",
    location: "",
    note: "",
    content: "",
    qrCode: "",
    eventInvationImage: "",
    postId: "",
  });
  const [rows, setRows] = useState([{ time: "", value: "" }]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [hasNewInvitationImage, setHasNewInvitationImage] = useState(false);
  const [invitationPreview, setInvitationPreview] = useState("");
  const [hasNewQrImage, setHasNewQrImage] = useState(false);
  const [qrPreview, setQrPreview] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState("invitation");

  const [posts, setPosts] = useState([]);
  const [editable, setEditable] = useState(false);

  const [useInvitationImage, setUseInvitationImage] = useState(false);

  useEffect(() => {
    ApiCustom.get("/posts?type=notification")
      .then((res) => setPosts(res?.data?.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setEditable(
      data != null
        ? hasPermission("EVENT_UPDATE")
        : hasPermission("EVENT_CREATE"),
    );
    if (data) {
      setFormData({
        title: data.title || "",
        topic: data.topic || "",
        time: data.time || "",
        location: data.location || "",
        note: data.note || "",
        content: data.content || "",
        qrCode: data.qrCode || "",
        eventInvationImage: data.eventInvationImage || "",
        postId: data.postId || eventPostId || "",
      });
      try {
        setRows(JSON.parse(data.content) || [{ time: "", value: "" }]);
      } catch {
        setRows([{ time: "", value: "" }]);
      }
      setUseInvitationImage(!!data.eventInvationImage);
    } else {
      setFormData({
        title: "",
        topic: "",
        time: "",
        location: "",
        note: "",
        content: "",
        qrCode: "",
        eventInvationImage: "",
        postId: eventPostId || "",
      });
      setRows([{ time: "", value: "" }]);
      setUseInvitationImage(false);
    }
    setErrors({});
    setHasNewInvitationImage(false);
    setInvitationPreview("");
    setHasNewQrImage(false);
    setQrPreview("");
  }, [data, eventPostId]);

  const handleChange = (e) => {
    const name = e?.name || e?.target?.name;
    const value = e?.value ?? e?.target?.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRowChange = (index, e) => {
    const { name, value } = e.target;
    const updatedRows = [...rows];
    updatedRows[index][name] = value;
    setRows(updatedRows);
    setFormData((prev) => ({ ...prev, content: JSON.stringify(updatedRows) }));
    setErrors((prev) => ({ ...prev, content: "" }));
  };

  const handleAddRow = () => {
    const updatedRows = [...rows, { time: "", value: "" }];
    setRows(updatedRows);
    setFormData((prev) => ({ ...prev, content: JSON.stringify(updatedRows) }));
  };

  const handleDeleteRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
    setFormData((prev) => ({ ...prev, content: JSON.stringify(updatedRows) }));
  };

  const openPicker = (target) => {
    setPickerTarget(target);
    setIsModalOpen(true);
  };
  const closePicker = () => setIsModalOpen(false);

  const handlePressImage = (media) => {
    setIsModalOpen(false);
    if (pickerTarget === "invitation") {
      setHasNewInvitationImage(true);
      setInvitationPreview(media.url);
      setFormData((prev) => ({ ...prev, eventInvationImage: media.url }));
      setErrors((prev) => ({ ...prev, eventInvationImage: "" }));
      setUseInvitationImage(true);
    } else {
      setHasNewQrImage(true);
      setQrPreview(media.url);
      setFormData((prev) => ({ ...prev, qrCode: media.url }));
      setErrors((prev) => ({ ...prev, qrCode: "" }));
    }
  };

  const getInvitationUrl = () => {
    if (hasNewInvitationImage) return invitationPreview;
    if (formData.eventInvationImage) return formData.eventInvationImage;
    return "";
  };
  const getQrUrl = () => {
    if (hasNewQrImage) return qrPreview;
    if (formData.qrCode) return formData.qrCode;
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const schema = useInvitationImage ? imageSchema : templateSchema;
      await schema.validate(formData, { abortEarly: false });

      if (!useInvitationImage) {
        if (
          rows.some(
            (item) => item.time.trim() === "" && item.value.trim() === "",
          )
        ) {
          setErrors((prev) => ({
            ...prev,
            content: "Vui lòng điền đầy đủ nội dung",
          }));
          setIsSubmitting(false);
          return;
        }
      }

      const payload = {
        ...formData,
        content: useInvitationImage
          ? formData.content
          : formData.content || JSON.stringify(rows),
        eventInvationImage: useInvitationImage
          ? getInvitationUrl() || null
          : null,
      };

      if (data) {
        await ApiCustom.put(`/event-update/${data.id}`, payload, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Cập nhật thành công");
      } else {
        await ApiCustom.post("/event-create", payload, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Thêm thành công");
      }
      onClose();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedPostTitle =
    posts.find((p) => `${p.id}` === `${formData.postId}`)?.title ||
    (formData.postId ? `#${formData.postId}` : "");

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="flex p-6 pb-4 justify-between items-center sticky top-0 bg-base-100 z-10">
          <h2 className="text-xl font-semibold mb-4">Nội dung thiệp mời</h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isSubmitting}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {data != null || hasPermission("EVENT_CREATE") ? (
            <div className="px-6">
              <div className="mb-4">
                <div className="inline-flex rounded-xl border overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => setUseInvitationImage(false)}
                    className={`px-4 py-2 text-sm transition ${
                      !useInvitationImage
                        ? "bg-primary text-white"
                        : "bg-base-100 hover:bg-base-200"
                    }`}
                  >
                    Dùng mẫu thiệp mời
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseInvitationImage(true)}
                    className={`px-4 py-2 text-sm transition ${
                      useInvitationImage
                        ? "bg-primary text-white"
                        : "bg-base-100 hover:bg-base-200"
                    }`}
                  >
                    Tải lên thiệp mời
                  </button>
                </div>
              </div>

              <div className={useInvitationImage ? "hidden" : "mb-4"}>
                <label htmlFor="title" className="block text-sm font-medium">
                  Tiêu đề<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${errors.title ? "border-red-500" : "border-gray-300"}`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              <div className={useInvitationImage ? "hidden" : "mb-4"}>
                <label htmlFor="topic" className="block text-sm font-medium">
                  Chủ đề<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${errors.topic ? "border-red-500" : "border-gray-300"}`}
                />
                {errors.topic && (
                  <p className="text-red-500 text-sm mt-1">{errors.topic}</p>
                )}
              </div>

              <div className={useInvitationImage ? "hidden" : "mb-4"}>
                <label htmlFor="time" className="block text-sm font-medium">
                  Thời gian<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${errors.time ? "border-red-500" : "border-gray-300"}`}
                />
                {errors.time && (
                  <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                )}
              </div>

              <div className={useInvitationImage ? "hidden" : "mb-4"}>
                <label htmlFor="location" className="block text-sm font-medium">
                  Địa điểm<i className="text-red-600"> *</i>
                </label>
                <TextEditor
                  canUpdate={editable}
                  readOnly={!editable}
                  disabled={!editable}
                  value={formData.location}
                  onChange={handleChange}
                  name="location"
                />
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                )}
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={useInvitationImage ? "hidden" : ""}>
                    <label className="block text-sm font-medium mb-1">
                      QR đăng ký
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div className="relative w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                        {getQrUrl() ? (
                          <img
                            src={getQrUrl()}
                            alt="QR"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <PhotoIcon className="w-10 h-10 text-gray-300" />
                          </div>
                        )}
                        {editable && (
                          <PencilSquareIcon
                            className="h-4 w-4 absolute bottom-1 right-1 cursor-pointer rounded-md"
                            onClick={() => openPicker("qr")}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={useInvitationImage ? "" : "hidden"}>
                    <label className="block text-sm font-medium mb-1">
                      Ảnh thiệp mời <i className="text-red-600"> *</i>
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div className="relative w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                        {getInvitationUrl() ? (
                          <img
                            src={getInvitationUrl()}
                            alt="Invitation"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <PhotoIcon className="w-10 h-10 text-gray-300" />
                          </div>
                        )}
                        {editable && (
                          <PencilSquareIcon
                            className="h-4 w-4 absolute bottom-1 right-1 cursor-pointer rounded-md"
                            onClick={() => openPicker("invitation")}
                          />
                        )}
                      </div>
                    </div>
                    {errors.eventInvationImage && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.eventInvationImage}
                      </p>
                    )}
                  </div>
                </div>

                <div className="w-full hidden">
                  <label className="block text-sm font-medium mb-1">
                    Hoạt động<i className="text-red-600"> *</i>
                  </label>
                  <input
                    type="text"
                    disabled
                    value={selectedPostTitle}
                    className={`w-full p-2 border rounded-md ${
                      errors.postId ? "border-red-500" : "border-gray-300"
                    } bg-base-200`}
                    placeholder="Chưa xác định"
                  />
                  {errors.postId && (
                    <p className="mt-1 text-sm text-red-600">{errors.postId}</p>
                  )}
                </div>
              </div>

              <div
                className={`${useInvitationImage ? "hidden" : ""} mb-4 mt-4`}
              >
                <label htmlFor="content" className="block text-sm font-medium">
                  Nội dung<i className="text-red-600"> *</i>
                </label>
                <div
                  className={`p-3 border mt-1 rounded ${errors.content ? "border-red-500" : "border-gray-300"}`}
                >
                  {rows.map((row, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end py-3"
                    >
                      <div className="hidden md:flex md:col-span-1 items-center justify-center font-semibold text-base mb-3">
                        {index + 1}
                      </div>
                      <div className="md:col-span-4">
                        <label className="block text-sm font-medium">
                          Thời gian
                        </label>
                        <input
                          disabled={!editable}
                          type="text"
                          name="time"
                          value={row.time}
                          onChange={(e) => handleRowChange(index, e)}
                          className="w-full p-2 border rounded border-gray-300"
                        />
                      </div>
                      <div className="md:col-span-6">
                        <label className="block text-sm font-medium">
                          Nội dung
                        </label>
                        <input
                          disabled={!editable}
                          type="text"
                          name="value"
                          value={row.value}
                          onChange={(e) => handleRowChange(index, e)}
                          className="w-full p-2 border rounded border-gray-300"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          disabled={!editable}
                          onClick={() => handleDeleteRow(index)}
                          className="h-10 px-3 btn btn-outline rounded text-red-500 btn-sm hover:text-red-500"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="w-full flex justify-end">
                    <button
                      type="button"
                      disabled={!editable}
                      onClick={handleAddRow}
                      className="mt-3 md:mt-4 px-4 py-2 bg-base-300 text-base rounded hover:bg-base-200"
                    >
                      Thêm dòng
                    </button>
                  </div>
                </div>
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
              </div>

              <div
                className={`${useInvitationImage ? "hidden" : ""} mb-4 mt-4`}
              >
                <label htmlFor="note" className="block text-sm font-medium">
                  Ghi chú
                </label>
                <TextEditor
                  canUpdate={editable}
                  readOnly={!editable}
                  disabled={!editable}
                  value={formData.note}
                  onChange={handleChange}
                  name="note"
                />
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-2">Chưa có sự kiện</p>
          )}

          <div className="flex p-6 justify-end space-x-4 sticky bottom-0 bg-base-100 z-10">
            <div className="space-x-5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 btn btn-sm rounded"
                disabled={isSubmitting}
              >
                {(hasPermission("EVENT_CREATE") && data == null) ||
                (hasPermission("EVENT_UPDATE") && data != null)
                  ? "Hủy"
                  : "Đóng"}
              </button>
              {((hasPermission("EVENT_CREATE") && data == null) ||
                (hasPermission("EVENT_UPDATE") && data != null)) && (
                <button
                  type="submit"
                  className={`px-4 py-2 btn btn-primary rounded btn-sm ${isSubmitting ? "loading" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-xs mr-2"></span>
                      Đang lưu...
                    </>
                  ) : (
                    "Lưu"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {isModalOpen && (
        <ImageManagerModal
          onClose={closePicker}
          onPress={handlePressImage}
          multiImage={true}
          selectedMultiMedia={[]}
          deActiveInsertImage={true}
        />
      )}
    </div>
  );
};

export default EventModal;
